import type {
  BatchUpdateLlgPoliciesRequest,
  Condition,
  Configuration,
  SearchLogsHistogramInput,
  SearchLogsInput,
} from '@repo/api-gw-sdk';
import {
  LlgPoliciesApi,
  LogSourcesApi,
  LogSourcesPoliciesApi,
  SearchApi,
} from '@repo/api-gw-sdk';
import type { PaginationState } from '@tanstack/react-table';

import type {
  EntryLogsQueryResponse,
  LogSpaceSummaryResponse,
} from '@/components/log-space/demo/types';
import { toJsonText } from '@/components/queryBuilder/QueryLanguageConverter';

import type { HttpClient } from './httpClient';

const baseUrl = '/logs';

export const logs = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const searchApi = new SearchApi(config);
  const logSourcesApi = new LogSourcesApi(config);
  const logSourcesPoliciesApi = new LogSourcesPoliciesApi(config);
  const llgPoliciesApi = new LlgPoliciesApi(config);

  return {
    useList: (
      pagination: PaginationState,
      condition: Condition | undefined,
      filter: string,
      dateRange: { startDate: string; endDate: string }
    ) =>
      httpClient.execute(
        (
          ...args: [
            string,
            {
              condition: Condition | undefined;
              pagination: PaginationState;
              filter?: string;
              dateRange: { startDate: string; endDate: string };
            },
          ]
        ) =>
          httpClient.fetch(
            `/projects/${args[0]}/logs/query?pageIndex=${args[1].pagination.pageIndex}&pageSize=${args[1].pagination.pageSize}${args[1].condition ? `&filter=${encodeURIComponent(toJsonText(args[1].condition))}` : undefined}&query=${args[1].filter ? encodeURIComponent(args[1].filter) : ''}&startDate=${encodeURIComponent(args[1].dateRange.startDate)}&endDate=${encodeURIComponent(args[1].dateRange.endDate)}`
          ) as Promise<EntryLogsQueryResponse>,
        baseUrl,
        '/query',
        currentProjectId,
        { condition, pagination, filter, dateRange }
      ),
    useListSchemas: (resourceIds: string[]) =>
      httpClient.execute(
        searchApi.getLogsSchemas.bind(searchApi),
        baseUrl,
        '/schemas',
        currentProjectId,
        resourceIds
      ),
    useLogsSummary: (
      query: Condition | undefined,
      filter: string,
      dateRange: { startDate: string; endDate: string }
    ) =>
      httpClient.execute(
        (
          ...args: [
            string,
            {
              query: Condition | undefined;
              filter: string;
              dateRange: { startDate: string; endDate: string };
            },
          ]
        ) =>
          httpClient.fetch(
            `/projects/${args[0]}/logs/summary?query=${args[1].filter ? encodeURIComponent(args[1].filter) : ''}${
              args[1].query
                ? `&filter=${encodeURIComponent(toJsonText(args[1].query))}`
                : ''
            }&startDate=${encodeURIComponent(args[1].dateRange.startDate)}&endDate=${encodeURIComponent(args[1].dateRange.endDate)}`
          ) as Promise<LogSpaceSummaryResponse>,
        baseUrl,
        '/summary',
        currentProjectId,
        { query, filter, dateRange }
      ),
    useSearch: (searchLogsInput: SearchLogsInput) =>
      httpClient.execute(
        (...args: Parameters<typeof searchApi.searchLogs>) =>
          args[1].resourceIds.length
            ? searchApi.searchLogs(...args)
            : Promise.resolve(undefined),
        baseUrl,
        '/search',
        currentProjectId,
        searchLogsInput
      ),
    useSources: () =>
      httpClient.execute(
        logSourcesApi.listLogSources.bind(logSourcesApi),
        baseUrl,
        '/sources',
        currentProjectId
      ),
    policies: {
      useList: (pageIndex?: number, pageSize?: number) =>
        httpClient.execute(
          logSourcesPoliciesApi.listLogSourcesPolicies.bind(
            logSourcesPoliciesApi
          ),
          baseUrl,
          '/policies',
          currentProjectId,
          pageIndex,
          pageSize
        ),
      update: (batchUpdateLlgPoliciesRequest: BatchUpdateLlgPoliciesRequest) =>
        llgPoliciesApi.batchUpdateLlgPolicies(
          currentProjectId,
          batchUpdateLlgPoliciesRequest
        ),
    },
    useHistogram: (input: SearchLogsHistogramInput) =>
      httpClient.execute(
        (...args: Parameters<typeof searchApi.getLogsHistogram>) =>
          args[1].resourceIds.length
            ? searchApi.getLogsHistogram(...args)
            : Promise.resolve(undefined),
        baseUrl,
        '/histogram',
        currentProjectId,
        input
      ),
  };
};
